/* eslint-disable indent */
import React, { useEffect } from 'react';
import { FormData, FormErrors, ICloseItemDialog } from './interfaces';
import {
  AppBar,
  Button,
  CustomTheme,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  FormControl,
  FormControlLabel,
  FormHelperText,
  FormLabel,
  IconButton,
  Radio,
  RadioGroup,
  TextField,
  Toolbar,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { Trans, useTranslation } from 'react-i18next';
import { atLeast, isEmpty } from '../../../../helpers/formHelpers';
import { enqueueSnackbar } from 'notistack';
import { MutateOptions, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import useFormState from '../../../../hooks/useFormState';
import useTrackFormChanges from '../../../../hooks/useTrackFormChanges';
import { Form } from '../../../../components/forms';
import { useCloseItem } from '../../../../reactQuery/opportunities';
import { ICloseItemParams } from '../../../../api/opportunitiesApi/interfaces';

export const CloseItemDialog: React.FC<ICloseItemDialog> = ({
  item,
  show,
  onClose,
}): JSX.Element => {
  const { t } = useTranslation();
  const queryClient = useQueryClient();
  const formId = 'closeItemForm';

  const { mutate: closeOpportunity, isLoading: isClosingOpportunity } =
    useCloseItem();

  const theme = useTheme<CustomTheme>();
  const fullScreen = useMediaQuery(theme.breakpoints.down('tablet'));

  const { formData, setFormFieldData, setFormData } = useFormState<FormData>(
    {}
  );
  const { formErrors, setFormErrors } = useTrackFormChanges<FormData>(formData);

  const validateForm = (): void => {
    const errors: FormErrors = {};

    if (!formData.result) {
      errors.result = t('errors.required');
    }

    if (formData.result === 'failed') {
      if (!formData.failureReason || isEmpty(formData.failureReason)) {
        errors.failureReason = t('errors.required');
      } else if (!atLeast(formData.failureReason, 10)) {
        errors.failureReason = t('errors.atLeast', {
          field: t('entities:opportunityItem.reason').toLowerCase(),
          min: 10,
        });
      }
    }

    setFormErrors(errors);
  };

  const handleSubmit = async () => {
    if (item && !Object.keys(formErrors).length) {
      const closeItemData: ICloseItemParams = {
        result: formData.result as ICloseItemParams['result'],
        reason: formData.failureReason as ICloseItemParams['reason'],
      };

      const mutateOptions:
        | MutateOptions<void, AxiosError, unknown>
        | undefined = {
        onSuccess: () => {
          enqueueSnackbar(t('opportunities:dialogs.closeOpportunity.success'), {
            variant: 'success',
          });

          queryClient.invalidateQueries({
            queryKey: ['opportunity-stages', { includeItems: true }],
          });

          onClose();
        },
        onError: () => {
          enqueueSnackbar(t('opportunities:dialogs.closeOpportunity.failed'), {
            variant: 'error',
          });
        },
      };

      closeOpportunity(
        {
          itemId: item.id,
          closeData: closeItemData,
        },
        mutateOptions
      );
    }
  };

  useEffect(() => {
    setFormErrors({});
  }, [show]);

  return (
    <Dialog
      fullScreen={fullScreen}
      open={show}
      keepMounted={false}
      //   TransitionComponent={Transition}
    >
      <AppBar sx={{ position: 'relative' }}>
        <Toolbar>
          <Typography sx={{ flex: 1 }} variant="h6" component="div">
            {t('opportunities:dialogs.closeOpportunity.title')}
          </Typography>
          <IconButton
            edge="end"
            color="inherit"
            disabled={isClosingOpportunity}
            onClick={onClose}
            aria-label="close"
          >
            <CloseIcon />
          </IconButton>
        </Toolbar>
      </AppBar>

      <DialogContent
        dividers={true}
        sx={{
          minWidth: fullScreen ? undefined : '400px',
          maxWidth: fullScreen ? undefined : '500px',
        }}
      >
        <DialogContentText
          sx={{
            mb: 2,
          }}
        >
          <Trans
            i18nKey="opportunities:dialogs.closeOpportunity.body"
            components={{ strong: <strong /> }}
            values={{ title: item?.title }}
          />
        </DialogContentText>

        <Form id={formId} onSubmit={handleSubmit}>
          <FormControl error={!!formErrors.result} required>
            <FormLabel id="radio-group-result">
              {t('opportunities:dialogs.closeOpportunity.fields.result.label')}
            </FormLabel>
            <RadioGroup
              aria-labelledby="radio-group-result"
              name="result"
              value={formData.result}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setFormFieldData(
                  'result',
                  (event.target as HTMLInputElement).value as FormData['result']
                )
              }
            >
              <FormControlLabel
                value="success"
                control={<Radio />}
                label={t(
                  'opportunities:dialogs.closeOpportunity.fields.result.options.success'
                )}
              />
              <FormControlLabel
                value="failed"
                control={<Radio />}
                label={t(
                  'opportunities:dialogs.closeOpportunity.fields.result.options.failed'
                )}
              />
            </RadioGroup>
            {!!formErrors.result && (
              <FormHelperText>{formErrors.result}</FormHelperText>
            )}
          </FormControl>

          {formData.result === 'failed' && (
            <TextField
              label={t(
                'opportunities:dialogs.closeOpportunity.fields.failureReason.label'
              )}
              variant="outlined"
              size="small"
              value={formData.failureReason}
              placeholder={t(
                'opportunities:dialogs.closeOpportunity.fields.failureReason.placeholder'
              )}
              slotProps={{
                inputLabel: {
                  shrink: true,
                },
              }}
              onChange={(e) =>
                setFormFieldData('failureReason', e.target.value)
              }
              error={!!formErrors.failureReason}
              helperText={formErrors.failureReason}
              multiline
              required
            />
          )}
        </Form>
      </DialogContent>

      <DialogActions>
        <Button onClick={onClose} disabled={isClosingOpportunity}>
          {t('buttons.cancel')}
        </Button>
        <Button
          form={formId}
          type="submit"
          variant="contained"
          onClick={validateForm}
          disabled={isClosingOpportunity}
        >
          {t('opportunities:dialogs.closeOpportunity.buttons.close')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};
